import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import axios, { AxiosResponse } from 'axios';

import MuiPhoneNumber from 'mui-phone-number';
import { MuiTelInput } from 'mui-tel-input';
import React from 'react';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { ShopLocationType } from '../../../interfaces/delivery-company/pickups';
import { API_ENDPOINT } from '../../../configurations/global.config';

import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import AddressInputWithAutocomplete from '../../common/adress-input-with-autocomplete/address-input-with-autocomplete';
import {
  CompactMerchantType,
  MerchantType,
} from '../../../interfaces/delivery-company/merchants';
import { RegionType, emptyRegion, regions } from '../../../data/regions';
import { isMobile, isTablet } from 'react-device-detect';
import { emptyPickupLocation } from '../../../data/pickup-locations';
import AreaInput from '../../common/city-input/city-input';
import Grid2 from '@mui/material/Unstable_Grid2';

export interface CreatePickupLocationFormProps {
  defaultPickupId?: string;
  merchant?: CompactMerchantType;
  successCallback?: (pickup: ShopLocationType) => void;
}

function CreatePickupLocationForm({
  defaultPickupId,
  merchant,
  successCallback,
}: CreatePickupLocationFormProps) {
  const [pickupData, setPickupData] = useState<ShopLocationType | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();

  const { user, config } = useContext(AuthContext);

  React.useEffect(() => {
    console.log('use effect');
    const getAndSetPickup = async () => {
      if (defaultPickupId !== undefined) {
        setIsLoading(true);
        try {
          const endpoint = `${API_ENDPOINT}/api/v1/pickup/${defaultPickupId}/`;
          console.log(endpoint);
          const response = await axios.get<ShopLocationType>(endpoint, config);
          console.log(response);
          setResponse(response);
          setPickupData(response.data);
        } catch (e) {
          setPickupData(undefined);
          if (axios.isAxiosError(e)) {
            setMessage('Failed to load pickup location data: ' + e.message);
          } else {
            setMessage('Failed to load pickup location data');
          }
          console.log(e);
        }
        setIsLoading(false);
      } else {
        setPickupData(emptyPickupLocation);
      }
    };
    getAndSetPickup();
  }, [defaultPickupId]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pickupData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving pickup location...');

      const requestData = {
        ...pickupData,
        merchant: merchant,
      };

      console.log('request data = ', requestData);

      try {
        let response;
        if (defaultPickupId !== undefined) {
          // update existing
          response = await axios.put(
            `${API_ENDPOINT}/api/v1/pickup/${defaultPickupId}/`,
            requestData,
            config
          );
          successCallback && successCallback(pickupData);
        } else {
          // create new
          response = await axios.post(
            `${API_ENDPOINT}/api/v1/pickups/`,
            requestData,
            config
          );
          successCallback && successCallback(response.data);
        }
        setResponse(response);
        if (response.status >= 200 && response.status < 300) {
          setMessage('Pickup location saved successfully');
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage(
            'Failed to save pickup location: ' + e.response?.statusText
          );
        } else {
          setMessage('Failed to save pickup location');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {pickupData?.id === ''
          ? 'Create Pickup Location'
          : 'Edit Pickup Location'}
      </Typography>

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message !== '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {pickupData && (
            <>
              <br />
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    disabled
                    value={merchant?.name}
                    label={'Merchant'}
                    required
                  />
                </FormControl>
                <br />
                <br />

                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    value={pickupData.name}
                    onChange={(e) =>
                      setPickupData({ ...pickupData, name: e.target.value })
                    }
                    label={'Pickup Name'}
                    required
                  />
                </FormControl>
                <br />
                <br />

              
                <AreaInput
                  area={pickupData.area ?? null}
                  setArea={(c) => c ? setPickupData({ ...pickupData, area: c }) : setPickupData({...pickupData, area: undefined})}
                />


                <br />

                <br />

                <TextField
                  autoComplete="off"
                  fullWidth
                  multiline
                  minRows={4}
                  value={pickupData.directions}
                  label={'Directions'}
                  onChange={(e) =>
                    setPickupData({ ...pickupData, directions: e.target.value })
                  }
                />
                <br />
                <br />
                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    value={pickupData.contact_person}
                    onChange={(e) =>
                      setPickupData({
                        ...pickupData,
                        contact_person: e.target.value,
                      })
                    }
                    label={'Contact Person'}
                    required
                  />
                </FormControl>
                <br />
                <br />

                <FormControl key={'phone-input'} fullWidth>
                  <MuiTelInput
                    autoComplete="off"
                    forceCallingCode
                    label="Contact Phone"
                    disabled={isLoading}
                    defaultCountry={'LB'}
                    variant="outlined"
                    required
                    value={pickupData.contact_phone}
                    onChange={(newPhone) => {
                      if (typeof newPhone === 'string') {
                        setPickupData({
                          ...pickupData,
                          contact_phone: newPhone,
                        });
                      }
                    }}
                  />
                </FormControl>
                <br />
                <br />

                <Button
                  size={(isMobile && !isTablet) ? 'small' : 'medium'}
                  variant="contained"
                  type={'submit'}
                >
                  Save
                </Button>
              </form>
            </>
          )}
        </>
      )}
    </>
  );
}

export default CreatePickupLocationForm;
